import * as THREE from 'three';

/**
 * Custom OBJ parser that handles large files by chunking geometry
 * to avoid WebGL index buffer limits (65k vertices)
 */
export class OBJParser {
  constructor(options = {}) {
    this.maxVerticesPerChunk = options.maxVerticesPerChunk || 60000;
  }

  /**
   * Load and parse OBJ from URL
   * @param {string} url - URL to the OBJ file
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<THREE.Group>}
   */
  async load(url, onProgress) {
    const response = await fetch(url);
    const text = await response.text();
    return this.parse(text, onProgress);
  }

  /**
   * Parse OBJ text content with support for large files
   * @param {string} text - OBJ file content
   * @param {Function} onProgress - Optional progress callback
   * @returns {THREE.Group}
   */
  parse(text, onProgress) {
    const vertices = [];
    const normals = [];
    const uvs = [];
    const faces = [];

    const lines = text.split('\n');
    const totalLines = lines.length;

    for (let i = 0; i < totalLines; i++) {
      const line = lines[i].trim();
      if (!line || line.startsWith('#')) continue;

      const parts = line.split(/\s+/);
      const type = parts[0];

      switch (type) {
        case 'v':
          vertices.push(parseFloat(parts[1]) || 0, parseFloat(parts[2]) || 0, parseFloat(parts[3]) || 0);
          break;
        case 'vn':
          normals.push(parseFloat(parts[1]) || 0, parseFloat(parts[2]) || 0, parseFloat(parts[3]) || 0);
          break;
        case 'vt':
          uvs.push(parseFloat(parts[1]) || 0, parseFloat(parts[2]) || 0);
          break;
        case 'f':
          const faceVerts = [];
          for (let j = 1; j < parts.length; j++) {
            const indices = parts[j].split('/');
            faceVerts.push({
              v: parseInt(indices[0]) - 1,
              vt: indices[1] ? parseInt(indices[1]) - 1 : -1,
              vn: indices[2] ? parseInt(indices[2]) - 1 : -1,
            });
          }
          // Triangulate (handle quads and n-gons)
          for (let j = 1; j < faceVerts.length - 1; j++) {
            faces.push(faceVerts[0], faceVerts[j], faceVerts[j + 1]);
          }
          break;
      }

      if (onProgress && i % 10000 === 0) {
        onProgress({ loaded: i, total: totalLines });
      }
    }

    const group = this.buildChunkedGeometry(vertices, normals, uvs, faces);

    console.log(
      `OBJ parsed: ${vertices.length / 3} vertices, ${faces.length / 3} triangles, ${group.children.length} chunks`
    );

    return group;
  }

  /**
   * Build chunked geometry from parsed data
   * @private
   */
  buildChunkedGeometry(vertices, normals, uvs, faces) {
    const group = new THREE.Group();
    const hasNormals = normals.length > 0;
    const hasUVs = uvs.length > 0;

    let chunkPositions = [];
    let chunkNormals = [];
    let chunkUVs = [];
    let chunkVertexCount = 0;
    let chunkIndex = 0;

    const createChunkMesh = () => {
      if (chunkPositions.length === 0) return;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(chunkPositions), 3));

      if (chunkNormals.length > 0) {
        geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(chunkNormals), 3));
      } else {
        geometry.computeVertexNormals();
      }

      if (chunkUVs.length > 0) {
        geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(chunkUVs), 2));
      }

      const material = new THREE.MeshStandardMaterial({
        color: 0xcccccc,
        side: THREE.DoubleSide,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = `obj_chunk_${chunkIndex}`;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);

      chunkIndex++;
      chunkPositions = [];
      chunkNormals = [];
      chunkUVs = [];
      chunkVertexCount = 0;
    };

    for (let i = 0; i < faces.length; i += 3) {
      if (chunkVertexCount + 3 > this.maxVerticesPerChunk) {
        createChunkMesh();
      }

      for (let j = 0; j < 3; j++) {
        const face = faces[i + j];
        const vIdx = face.v * 3;

        chunkPositions.push(vertices[vIdx], vertices[vIdx + 1], vertices[vIdx + 2]);

        if (hasNormals && face.vn >= 0) {
          const nIdx = face.vn * 3;
          chunkNormals.push(normals[nIdx], normals[nIdx + 1], normals[nIdx + 2]);
        }

        if (hasUVs && face.vt >= 0) {
          const tIdx = face.vt * 2;
          chunkUVs.push(uvs[tIdx], uvs[tIdx + 1]);
        }

        chunkVertexCount++;
      }
    }

    createChunkMesh();

    return group;
  }
}

export const objParser = new OBJParser();
